import { useContext } from "react";
import { AiOutlineProject } from "react-icons/ai";
import {
  BiBarChart,
  BiBarChartAlt2,
  BiCloudRain,
  BiGitCommit,
  BiMailSend,
  BiSupport,
  BiUserPin
} from "react-icons/bi";
import {
  BsBuilding,
  BsFileEarmarkText,
  BsHouse,
  BsLaptop,
  BsSpeedometer,
  BsSuitcaseLg
} from "react-icons/bs";
import { CgBot } from "react-icons/cg";
import { FaHistory } from "react-icons/fa";
import {
  FiAlertTriangle,
  FiFile,
  FiFolder,
  FiHome,
  FiList,
  FiMinimize,
  FiPlusSquare,
  FiSettings,
  FiTool,
  FiTruck,
  FiUsers
} from "react-icons/fi";
import { MdOutlinePersonalInjury } from "react-icons/md";
import { RegulationContext } from "../../../App";
import AIimage from "../../../images/esg_ai_Green.png";
import useHasPermission from "../../hooks/useHasPermission";
import {
  getAllowedSidebarNavKeys,
  hasGoalsKpiNavAccess
} from "../../../pages/SubscriberSupplierAdmin/GoalsAndKpi/utils/goalsAndKpiNavAccess";
import { buildHierarchyPermissions } from "../../../pages/SubscriberSupplierAdmin/GoalsAndKpi/utils/hierarchyPermissions";
import { REPORTS_NAV_VISIBLE } from "../../../pages/SubscriberSupplierAdmin/GoalsAndKpi/utils/goalsAndKpiConstants";
import { LS_AUTHORITY_KEY, LS_UI_ELEMENTS_CONFIG_KEY } from "../common";

export function SidebarMenuCompanyAdmin_LKSG({ t }) {
  const { glbRegulation } = useContext(RegulationContext);
  const uiElementsConfig = JSON.parse(localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY)) ?? null;
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const { hasDMPeriodicViewPermission, hasDMOneTimeViewPermission,
    hasGoalsAndKPIsViewPermission, hasDoubleMaterialityViewPermission, hasGHGEmissionsViewPermission,
    hasHomeViewPermission, hasDashboardViewPermission, hasDataCollectionViewPermission,
    hasConfigViewPermission, hasUserViewPermission, hasSettingsViewPermission,
    hasGRIViewPermission, hasBRSRViewPermission, hasCSRViewPermission,
    hasVSMEViewPermission, hasGHGReportViewPermission, hasGHGEmissionsUpdatePermission } = useHasPermission();
  const goalsKpiNavPerms = buildHierarchyPermissions(
    Array.isArray(lsData?.roles) ? lsData.roles : []
  );
  const { canManageMetadata, canViewFullAudit, availableDashboardViews, defaultDashboardViewId } =
    goalsKpiNavPerms;
  const allowedNav = getAllowedSidebarNavKeys(goalsKpiNavPerms);
  const canShowNav = (navKey) => allowedNav.has(navKey);
  // TEMPORARY: same Goals & KPI nav check used to hide the rest of the app chrome. Revisit soon.
  const restrictNavToGoalsKpi = hasGoalsKpiNavAccess(goalsKpiNavPerms);

  if (glbRegulation?.type === 'R') {
    const menuItems = [
      {
        text: t("DataCollectionEvents.label"),
        link: "assessments",
        icon: FiFile,
        onClick: null,
      },
      {
        text: t("Incidents.label"),
        link: "incident_list",
        icon: MdOutlinePersonalInjury,
        onClick: null,
      },
      ...(hasDMPeriodicViewPermission() || hasDMOneTimeViewPermission() ? [
        {
          text: t("DataManagement.label"),
          link: "risk_supplier_list",
          icon: FiAlertTriangle,
          onClick: null,
        }] : []),
      ...(hasGRIViewPermission() ||
        hasBRSRViewPermission() ||
        hasCSRViewPermission() ||
        hasVSMEViewPermission() ||
        hasGHGReportViewPermission() ? [{
          text: t("ReportingStudio.label"),
          link: "reporting_list",
          icon: BsFileEarmarkText,
          onClick: "reporting_list",
        }] : []),
      ...(hasConfigViewPermission() ? [
        {
          text: t("Configurations.label"),
          link: "configurations",
          icon: FiTool,
          onClick: null,
        }] : []),

      {
        text: t("Suppliers.label"),
        link: "list_supplier",
        icon: FiTruck,
        onClick: null,
      },
      {
        text: t("Settings.label"),
        link: "supplier_type",
        icon: FiSettings,
        onClick: "settings",
        children: [
          {
            text: t("Company.label"),
            link: "company_settings",
            icon: BsBuilding,
            onClick: null,
          },
          {
            text: t("Forms.label"),
            link: "forms",
            icon: FiFile,
            onClick: null,
          },
          {
            text: t("UnitWiseSettings.label"),
            link: "unit_wise",
            icon: BiBarChart,
            onClick: null,
          },
          {
            text: t("Materials.label"),
            link: "supply_materials",
            icon: FiMinimize,
            onClick: "supply_materials",
          },
          {
            text: t("SupplierTypes.label"),
            link: "supplier_type",
            icon: BiUserPin,
            onClick: "settings",
          },
          ...(hasUserViewPermission() ? [
            {
              text: t("Users.label"),
              link: "users",
              icon: FiUsers,
              onClick: null,
            }] : [])
        ],
      },
      {
        text: t("Support.label"),
        link: "support",
        icon: BiSupport,
        onClick: null,
      },
    ];
    return menuItems;
  } else {
    let menuItems = [];

    // TEMPORARY: when Goals & KPI nav access is present, skip every top-level item except Goals & KPI and Settings. Revisit soon.
    if (!restrictNavToGoalsKpi) {
    if (lsData?.profile?.company?.legalName === "Roads & Transport Authority") {
      menuItems.push({
        text: t("ExecutiveDashboard.label"),
        link: "http://localhost:8080/idashboards/view?dashId=871",
        isExecutive: true,
        icon: BsSuitcaseLg,
        onClick: null,
      });
    }

    menuItems.push(
      {
        text: t("Home.label"),
        link: "home",
        icon: BsHouse,
        onClick: null,
        defaultActive: true,
      },
      ...(hasDashboardViewPermission() ? [{
        text: uiElementsConfig?.texts?.dashboard ?? t("Dashboard.label"),
        link: "dashboard",
        icon: BsLaptop,
        onClick: null,
        defaultActive: true,
      }] : []),
      // Superseded by the new lazy-loaded Goals & KPI module (menuItems.push near the bottom of this
      // function) — kept here, commented out, rather than deleted; old /statusview & /kpilist pages
      // and their routes are untouched.
      // {
      //   text: t("GoalsandKPIs.label"),
      //   link: null,
      //   icon: BsSpeedometer,
      //   showDiamondIcon: !(hasGoalsAndKPIsViewPermission()),
      //   onClick: null,
      //   children: [
      //     {
      //       text: t("Status.label"),
      //       link: "statusview",
      //       icon: BiGitCommit,
      //       onClick: "statusview",
      //     },
      //     {
      //       text: t("Configure.label"),
      //       link: "kpilist",
      //       icon: BiGitCommit,
      //       onClick: "kpilist",
      //     }
      //   ]
      // },
    );

    menuItems.push(
      ...hasDataCollectionViewPermission() ? [{
        text: t("DataCollection.label"),
        link: null,
        icon: FiFile,
        onClick: null,
        children: [
          {
            text: t("Events.label"),
            link: "assessments",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("ImportData.label"),
            link: "import_wizard",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("Scope3DataCollection.label"),
            link: "ghg_emissions",
            icon: BiGitCommit,
            onClick: null,
          },
        ]
      }] : []
    )


    menuItems = [
      ...menuItems,
      ...(hasDMPeriodicViewPermission() || hasDMOneTimeViewPermission() ? [
        {
          text: t("DataManagement.label"),
          link: null,
          icon: FiPlusSquare,
          onClick: null,
          children: [
            ...(hasDMPeriodicViewPermission() ? [{
              text: t("Periodic.label"),
              link: "dataview",
              icon: BiGitCommit,
              onClick: null,
            }] : []),
            ...(hasDMOneTimeViewPermission() ? [{
              text: t("OneTime.label"),
              link: "qualitative",
              icon: BiGitCommit,
              img: AIimage,
            }] : []),
            // {
            //   text: t("Anomaly.label"),
            //   link: null,
            //   icon: BiGitCommit,
            //   onClick: null,
            //   children: [
            //     { text: t("AnomalyDashboard.label"),  link: "anomalies/dashboard",    icon: BiGitCommit },
            //     { text: t("AnomalyList.label"),       link: "anomalies/list",         icon: BiGitCommit },
            //     { text: t("AnomalyRuleConfig.label"), link: "anomalies/rule_configs", icon: BiGitCommit },
            //   ],
            // },
          ]
        }] : []),
    ];

    menuItems = [
      ...menuItems,
      ...(hasGRIViewPermission() ||
        hasBRSRViewPermission() ||
        hasCSRViewPermission() ||
        hasVSMEViewPermission() ||
        hasGHGReportViewPermission() ? [{
          text: t("ReportingStudio.label"),
          link: "reporting_list",
          icon: BsFileEarmarkText,
          onClick: "reporting_list",
          img: AIimage,
        }] : []),
    ];

    menuItems = [
      ...menuItems,
      {
        text: t("Materiality.label"),
        link: null,
        icon: BiBarChartAlt2,
        onClick: !(hasDoubleMaterialityViewPermission()) ? "double-materiality-upsale" : null,
        showDiamondIcon: !(hasDoubleMaterialityViewPermission()),
        children: [
          {
            text: t("Overview.label"),
            link: "dmat/stats",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("StakeHolders.label"),
            link: "dmat/stakeholders",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("Issues.label"),
            link: "dmat/issues",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("Surveys.label"),
            link: "dmat/matassessments",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("ScoreCard.label"),
            link: "dmat/scorecard/default",
            icon: BiGitCommit,
            onClick: null,
          },
          {
            text: t("Report.label"),
            link: "dmat/report",
            icon: BiGitCommit,
            onClick: null,
          }
        ]
      },
    ];

    menuItems = [
      ...menuItems,
      {
        text: t("CarbonFootPrint.label"),
        link: null,
        icon: BiCloudRain,
        menuKey: "ghg-emissions",
        onClick: !(hasGHGEmissionsViewPermission()) ? "ghg-emissions-upsale" : null,
        showDiamondIcon: !(hasGHGEmissionsViewPermission()),
        children: [
          {
            text: t("GHGProtocol.label"),
            link: null,
            icon: BiGitCommit,
            menuKey: "ghg-2",
            persistOpen: true,
            children: [
              {
                text: t("Overview.label"),
                link: "ghg/overview",
                icon: BiGitCommit,
                onClick: null,
              },
              {
                text: t("GHGInventory.label"),
                link: "ghg_inventory",
                icon: BiGitCommit,
                onClick: null,
              },
              {
                text: t("DataManagement.label"),
                link: "collect_data",
                icon: BiGitCommit,
                onClick: null,
              },
              {
                text: t("GHGReport.label"),
                link: "ghg_inventory_report",
                icon: BiGitCommit,
                onClick: null,
              },
            ],
          },
          {
            text: t("UAEFDL.label"),
            link: null,
            icon: BiGitCommit,
            onClick: null,
            menuKey: "ghg-1",
            persistOpen: false,
            children: [
              {
                text: t("Overview.label"),
                link: "uaefdl/overview",
                icon: BiGitCommit,
                onClick: null,
              },
              {
                text: t("UAEFDLEmissionsInventory.label"),
                link: "uaefdl_inventory",
                icon: BiGitCommit,
                onClick: null,
              },
              {
                text: t("DataManagement.label"),
                link: "uaefdl_collect_data",
                icon: BiGitCommit,
                onClick: null,
              },
              {
                text: t("UAEFDLReport.label"),
                link: "uaefdl_inventory_report",
                icon: BiGitCommit,
                onClick: null,
              },
            ],
          },
        ],
      },
    ];

    menuItems = [
      ...menuItems,
      ...(hasConfigViewPermission() ? [
        {
          text: t("Configurations.label"),
          link: null,
          icon: FiTool,
          onClick: null,
          children: [
            {
              text: t("dataPoints.label"),
              link: 'standard',
              icon: BiGitCommit,
              onClick: 'standard',
            },
            {
              text: t("DataSource.label"),
              link: 'dsconfig',
              icon: BiGitCommit,
              onClick: 'dsconfig',
            },
            {
              text: t("DataSourceMappings.label"),
              link: 'dsmaps',
              icon: BiGitCommit,
              onClick: 'dsmaps',
            }
          ]
        }] : []),
    ];

    }

    // TEMPORARY: when Goals & KPI nav access is present, Settings shows only Company and Users. Revisit soon.
    let settingsChildren = [
      {
        text: t("Company.label"),
        link: "company_settings",
        icon: BsBuilding,
        onClick: null,
      },
    ];

    if (!restrictNavToGoalsKpi) {
      settingsChildren.push(
        {
          text: t("ESGAICopilot.label"),
          link: "ESGAI",
          icon: CgBot,
          onClick: null,
        },
        {
          text: t("ReportSettings.label"),
          link: "Report",
          icon: BsFileEarmarkText,
          onClick: null,
        },
        {
          text: t("Forms.label"),
          link: "forms",
          icon: FiFile,
          onClick: null,
        },
        {
          text: t("UnitWiseSettings.label"),
          link: "unit_wise",
          icon: BiBarChart,
          onClick: null,
        },
      );
    }

    if (!uiElementsConfig || uiElementsConfig?.hiddenModules?.indexOf('SETTINGS_USERS') < 0) {
      settingsChildren.push(
        ...hasUserViewPermission() ? [{
          text: t("Users.label"),
          link: "users",
          icon: FiUsers,
          onClick: null,
        }] : []);
    }

    if (!restrictNavToGoalsKpi) {
      settingsChildren.push({
        text: t("EmissionFactors.label"),
        link: "ghg/datasets",
        icon: FiSettings,
        onClick: null,
      });
    }

    // Goals & KPI module — VIEW_GOALS_KPI + a visibility tier role; no menu when only entitlement.
    const goalsKpiAdminChildren = canManageMetadata
      ? [
        // {
        //   text: t("GoalsAndKpi.sidebar.administrationOverview.label"),
        //   link: "goals_kpi/admin",
        //   icon: BiGitCommit,
        //   onClick: "goals_kpi/admin"
        // },
        // ...(REPORTS_NAV_VISIBLE
        //   ? [
        //     {
        //       text: t("GoalsAndKpi.sidebar.reportBuilder.label"),
        //       link: "goals_kpi/rptbuilder",
        //       icon: BiGitCommit,
        //       onClick: "goals_kpi/rptbuilder"
        //     }
        //   ]
        //   : []),
        // {
        //   text: t("GoalsAndKpi.sidebar.dataImport.label"),
        //   link: "goals_kpi/import",
        //   icon: BiGitCommit,
        //   onClick: "goals_kpi/import"
        // },
        {
          text: t("GoalsAndKpi.sidebar.settings.label"),
          link: "goals_kpi/settings",
          icon: BiGitCommit,
          onClick: "goals_kpi/settings"
        },
        {
          text: t("GoalsAndKpi.sidebar.frameworks.label"),
          link: "goals_kpi/frameworks",
          icon: BiGitCommit,
          onClick: "goals_kpi/frameworks"
        }
      ]
      : [];
    const dashboardNavViews = Array.isArray(availableDashboardViews) ? availableDashboardViews : [];
    const dashboardNavItem =
      dashboardNavViews.length > 1
        ? {
            navKey: "dashboard",
            text: t("GoalsAndKpi.sidebar.dashboard.label"),
            link: "goals_kpi/dashboard",
            icon: BiGitCommit,
            onClick: null,
            children: dashboardNavViews.map((view) => ({
              text: t(`GoalsAndKpi.sidebar.dashboardView.${view.id}.label`),
              link: "goals_kpi/dashboard",
              search: `?view=${view.id}`,
              matchQuery: { view: view.id },
              activeWhenNoQuery: view.id === defaultDashboardViewId,
              menuKey: `goals-kpi-dashboard-${view.id}`,
              icon: BiGitCommit,
              onClick: null
            }))
          }
        : {
            navKey: "dashboard",
            text: t("GoalsAndKpi.sidebar.dashboard.label"),
            link: "goals_kpi/dashboard",
            icon: BiGitCommit,
            onClick: "goals_kpi/dashboard"
          };
    const goalsKpiNavItems = [
      dashboardNavItem,
      {
        navKey: "tasks",
        text: t("GoalsAndKpi.widget.myTasks.label"),
        link: "goals_kpi/tasks",
        icon: BiGitCommit,
        onClick: "goals_kpi/tasks"
      },
      {
        navKey: "goals",
        text: t("GoalsAndKpi.sidebar.goalExplorer.label"),
        link: "goals_kpi/goals",
        icon: BiGitCommit,
        onClick: "goals_kpi/goals"
      },
      {
        navKey: "hierarchy",
        text: t("GoalsAndKpi.sidebar.hierarchyExplorer.label"),
        link: "goals_kpi/hierarchy",
        icon: BiGitCommit,
        onClick: "goals_kpi/hierarchy"
      },
      {
        navKey: "plans",
        text: t("GoalsAndKpi.sidebar.planManagement.label"),
        link: "goals_kpi/plans",
        icon: BiGitCommit,
        onClick: "goals_kpi/plans"
      },
      {
        navKey: "approvals",
        text: t("GoalsAndKpi.sidebar.approvals.label"),
        link: "goals_kpi/approvals",
        icon: BiGitCommit,
        onClick: "goals_kpi/approvals"
      },
      {
        navKey: "my_strategic_objectives",
        text: t("GoalsAndKpi.sidebar.myStrategicObjectives.label"),
        link: "goals_kpi/my_strategic_objectives",
        icon: BiGitCommit,
        onClick: "goals_kpi/my_strategic_objectives"
      },
      {
        navKey: "my_strategic_initiatives",
        text: t("GoalsAndKpi.sidebar.myStrategicInitiatives.label"),
        link: "goals_kpi/my_strategic_initiatives",
        icon: BiGitCommit,
        onClick: "goals_kpi/my_strategic_initiatives"
      },
      {
        navKey: "my_at_risk_items",
        text: t("GoalsAndKpi.sidebar.myAtRiskItems.label"),
        link: "goals_kpi/my_at_risk_items",
        icon: BiGitCommit,
        onClick: "goals_kpi/my_at_risk_items"
      },
      {
        navKey: "team_tasks_budget_detail",
        text: t("GoalsAndKpi.sidebar.teamTasksBudgetDetail.label"),
        link: "goals_kpi/team_tasks_budget_detail",
        icon: BiGitCommit,
        onClick: "goals_kpi/team_tasks_budget_detail"
      },
      // {
      //   navKey: "reports",
      //   text: t("GoalsAndKpi.sidebar.reports.label"),
      //   link: "goals_kpi/reports",
      //   icon: BiGitCommit,
      //   onClick: "goals_kpi/reports"
      // },
      {
        navKey: "budget",
        text: t("GoalsAndKpi.sidebar.budget.label"),
        link: "goals_kpi/budget",
        icon: BiGitCommit,
        onClick: "goals_kpi/budget"
      },
      {
        navKey: "audit",
        text: t("GoalsAndKpi.sidebar.audit.label"),
        link: "goals_kpi/audit",
        icon: BiGitCommit,
        onClick: "goals_kpi/audit"
      },
      // {
      //   navKey: "analytics",
      //   text: t("GoalsAndKpi.sidebar.analytics.label"),
      //   link: "goals_kpi/analytics",
      //   icon: BiGitCommit,
      //   onClick: "goals_kpi/analytics"
      // },
      {
        navKey: "notifications",
        text: t("GoalsAndKpi.sidebar.notifications.label"),
        link: "goals_kpi/notifications",
        icon: BiGitCommit,
        onClick: "goals_kpi/notifications"
      },
      {
        navKey: "my_escalations",
        text: t("GoalsAndKpi.sidebar.myEscalations.label"),
        link: "goals_kpi/my_escalations",
        icon: BiGitCommit,
        onClick: "goals_kpi/my_escalations"
      },
      {
        navKey: "alerts_escalations",
        text: t("GoalsAndKpi.sidebar.alertsEscalations.label"),
        link: "goals_kpi/alerts_escalations",
        icon: BiGitCommit,
        onClick: "goals_kpi/alerts_escalations"
      }
    ];

    const goalsKpiChildren = [
      ...goalsKpiNavItems
        .filter((item) => {
          if (item.navKey === "audit") {
            return canShowNav("audit") && canViewFullAudit;
          }
          return canShowNav(item.navKey);
        })
        .map(({ navKey, ...item }) => item),
      ...(goalsKpiAdminChildren.length
        ? [
          {
            text: t("GoalsAndKpi.sidebar.administration.label"),
            link: null,
            icon: BiGitCommit,
            onClick: null,
            children: goalsKpiAdminChildren
          }
        ]
        : [])
    ];

    if (hasGoalsKpiNavAccess(goalsKpiNavPerms) && goalsKpiChildren.length > 0) {
      menuItems.push({
        text: t("GoalsandKPIs.label"),
        link: null,
        icon: BsSpeedometer,
        onClick: null,
        children: goalsKpiChildren
      });
    }

    if (hasSettingsViewPermission()) {
      menuItems.push({
        text: t("Settings.label"),
        link: "supplier_type",
        icon: FiSettings,
        onClick: "settings",
        children: settingsChildren,
      });
    }

    if (!restrictNavToGoalsKpi) {
      settingsChildren.push({
        text: t("ConversionFactor.label"),
        link: "uom",
        icon: FiSettings,
        onClick: null,
      });
      settingsChildren.push({
        text: t("EmailLog.label"),
        link: "email_log",
        icon: FaHistory,
        onClick: null
      });
      settingsChildren.push({
        text: t("Anomaly.label"),
        link: null,
        icon: BiGitCommit,
        onClick: null,
        children: [
          { text: t("AnomalyDashboard.label"), link: "anomalies/dashboard", icon: BiGitCommit },
          { text: t("AnomalyList.label"), link: "anomalies/list", icon: BiGitCommit },
          { text: t("AnomalyRuleConfig.label"), link: "anomalies/rule_configs", icon: BiGitCommit },
        ],
      });
    }

    if (!restrictNavToGoalsKpi && (!uiElementsConfig || uiElementsConfig?.hiddenModules?.indexOf('SUPPORT') < 0)) {
      menuItems.push({
        text: t("Support.label"),
        link: "support",
        icon: BiSupport,
        onClick: null,
      });
    }
    return menuItems;
  }

}
export function SidebarMenuSupplierAdmin_LKSG({ t }) {
  const { hasUserViewPermission, hasSettingsViewPermission, hasGRIViewPermission, hasBRSRViewPermission, hasCSRViewPermission, hasVSMEViewPermission, hasGHGReportViewPermission } = useHasPermission();
  const menuItems = [
    {
      text: t("Home.label"),
      link: "dashboard",
      icon: FiHome,
      onClick: null,
    },
    {
      text: t("Clients.label"),
      link: "clients",
      icon: FiTruck,
      onClick: null,
    },
    {
      text: t("PendingInvitations.label"),
      link: "pending_invitations",
      icon: BiMailSend,
      onClick: null,
    },
    {
      text: t("DataCollectionEvents.label"),
      link: "assessments",
      icon: FiFile,
      onClick: null,
    },
    {
      text: t("Documents.label"),
      link: "documents",
      icon: FiFolder,
      onClick: null,
    },
    {
      text: t("Tasks.label"),
      link: "tasks",
      icon: FiList,
      onClick: "tasks"
    },
    {
      text: t("Incidents.label"),
      link: "incident_list",
      icon: MdOutlinePersonalInjury,
      onClick: null,
    },
    {
      text: t("Materials.label"),
      link: "supply_materials",
      icon: FiMinimize,
      onClick: null,
      disabled: true
    },
    {
      text: t("Reports.label"),
      link: "reports_list",
      icon: AiOutlineProject,
      onClick: "reports_list",
      children: [
        ...(hasGRIViewPermission() ||
          hasBRSRViewPermission() ||
          hasCSRViewPermission() ||
          hasVSMEViewPermission() ? [{
            text: t("ReportingStudio.label"),
            link: "reporting_list",
            icon: BsFileEarmarkText,
            onClick: null,
          }] : []),
      ],
    },
    {
      ...hasSettingsViewPermission() ? {
        text: t("Settings.label"),
        link: "settings",
        icon: FiSettings,
        onClick: "settings",
        children: [
          ...hasUserViewPermission() ? [
            {
              text: t("Users.label"),
              link: "users",
              icon: FiUsers,
              onClick: null,
            },
          ] : []
        ],
      } : {}
    },
    ...hasUserViewPermission() ? [
      {
        text: t("Users.label"),
        link: "users",
        icon: FiUsers,
        onClick: null,
      },
    ] : [],
    {
      text: t("Support.label"),
      link: "support",
      icon: BiSupport,
      onClick: null,
    },
  ];
  return menuItems;
}
